import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnDestroy, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { SHIELD_PATHS } from '@bn/app-shield/constants';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { IsElementStuckDirective } from '@bn/shared/directives';
import { ContentService } from '@bn/shared/services';
import { StoreRequest } from '@bn/shared/state-store';
import { RegexUtils } from '@bn/shared/utils';
import { MarkAllTouchedDirective } from '@bn/web-ui';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { InputComponent } from '@bn/web-ui/input/input.component';
import { LoadingComponent } from '@bn/web-ui/loading/loading.component';
import { SelectOptions } from '@bn/web-ui/select/models';
import { SelectComponent } from '@bn/web-ui/select/select.component';
import { FormcontrolErrorsComponent } from '@bn/web-ui/standalone/formcontrol-errors/formcontrol-errors.component';
import {
  CreateWafRateLimitRequest,
  RateLimitRule,
  RatelimitRuleActionType,
  WafMappedEnumList,
  WafRatelimitBlockType,
  WafRatelimitCounterKeyType,
  WafRateLimitTimeframeType,
  WafRuleOperatorType,
  WafRuleTransformationType,
} from '../../openapi';
import { MapWafEnumToSelectItems } from '../../pipes';
import { ShieldApiStore } from '../../stores';
import { cleanRuleDescription } from '../../utils';

@Component({
  templateUrl: './app-shield-rate-limit-add.page.html',
  styleUrls: ['./app-shield-rate-limit-add.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MapWafEnumToSelectItems,
    IsElementStuckDirective,
    NgClass,
    ButtonComponent,
    CardComponent,
    FormcontrolErrorsComponent,
    InputComponent,
    LoadingComponent,
    SelectComponent,
    FaIconComponent,
    ReactiveFormsModule,
    MarkAllTouchedDirective,
  ],
})
export class AppShieldRateLimitAddPage implements OnDestroy {
  #contentService = inject(ContentService);
  #draftRule: Partial<CreateWafRateLimitRequest | RateLimitRule> | undefined;
  #fb = inject(FormBuilder);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #routeParams = toSignal(this.#route.params);
  #shieldStore = inject(ShieldApiStore);

  isAddFinished: Signal<boolean> = this.#shieldStore.isFinished(StoreRequest.Add, ['rateLimits']);
  isUpdateFinished: Signal<boolean> = this.#shieldStore.isFinished(StoreRequest.Update, ['rateLimits']);
  isUpdateLoading = this.#shieldStore.isLoading(StoreRequest.Update, ['rateLimits']);
  isSaveLoading = this.#shieldStore.isLoading(StoreRequest.Add, ['rateLimits']);
  isPremium = false;
  rateLimitId = 0;
  shield = this.#shieldStore.selectedEntity();
  wafRuleVariableTypes?: WafMappedEnumList;
  wafRuleOperatorType?: WafMappedEnumList;
  wafRateLimitTimeframeType?: WafMappedEnumList;
  wafRatelimitBlockType?: WafMappedEnumList;
  wafRuleActionType?: WafMappedEnumList;
  wafRuleTransformationType?: WafMappedEnumList;
  wafRatelimitCounterKeyType?: WafMappedEnumList;

  options: Partial<SelectOptions> = {
    clearable: false,
    closeOnSelect: false,
    dropdownWidth: '100',
    hideClear: true,
    hideName: false,
    hideRequiredLabel: true,
    isSearchable: false,
    minWidth: '100px',
    multiple: true,
    placeholder: 'Select Transformations',
    showOnlyCount: false,
  };

  requestMatchers = this.#fb.array([this.createMatcherForm()]);

  form = this.#fb.group({
    Name: this.#fb.control<string>('', [Validators.required, Validators.pattern(RegexUtils.LettersAndNumbersRegex), Validators.maxLength(100)]),
    Description: this.#fb.control<string>('', [Validators.pattern(RegexUtils.LettersAndNumbersRegex), Validators.maxLength(500)]),
    requestMatchers: this.requestMatchers,
    Transformations: this.#fb.control<WafRuleTransformationType[]>([]),
    CounterKeyType: this.#fb.control<WafRatelimitCounterKeyType | null>(null, [Validators.required]),
    RateExceedingRequests: this.#fb.control<number | null>(null, [Validators.required]),
    RateExceedingTimeframe: this.#fb.control<WafRateLimitTimeframeType | null>(null, [Validators.required]),
    ResponseAction: this.#fb.control<RatelimitRuleActionType | null>(null, [Validators.required]),
    ResponseActionTimeframe: this.#fb.control<WafRatelimitBlockType | null>(null, [Validators.required]),
  });

  // make signals out of observables, to reduce code, add debounceTime if needed if signal goes crazy
  #requestMatchersValue = toSignal(this.requestMatchers.valueChanges);
  #previousValues = this.#requestMatchersValue();

  constructor() {
    const pullZoneId = this.#shieldStore.pullZoneId();

    this.#contentService.setTitle('Add rate limit rule');
    this.#contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}/${SHIELD_PATHS.ShieldRateLimit}`,
    });

    this.setWafEnums();
    this.listenToStoreSignals();
    this.listenToRouteParams();
    this.listenToRequestMatchersChanges();
  }

  ngOnDestroy(): void {
    this.#contentService.setBackButton(null);
  }

  canDeactivate(): boolean {
    return !this.form.dirty;
  }

  patchRateLimitForm(): void {
    const ruleSettings = this.#draftRule?.ruleConfiguration;
    const [variableKey, variableValue] = Object.entries(ruleSettings?.variableTypes ?? {})?.[0] ?? {};
    const conditionsVariable = this.wafRuleVariableTypes?.enumValues?.find(({ name }) => name === variableKey)?.value;

    // set main matcher, form already exists
    this.requestMatchers.at(0).patchValue({
      ConditionsVariable: conditionsVariable ?? null,
      ConditionsOperator: ruleSettings?.operatorType ?? null,
      VariableValue: variableValue ?? null,
      OperatorValue: ruleSettings?.value ?? null,
    });

    // set chained matchers, the same way as main one, but append new forms
    ruleSettings?.chainedRuleConditions?.forEach((chainMatcher) => {
      const [variableKey, variableValue] = Object.entries(chainMatcher?.variableTypes ?? {})?.[0] ?? {};
      const conditionsVariable = this.wafRuleVariableTypes?.enumValues?.find(({ name }) => name === variableKey)?.value;

      this.onAddMatcher({
        ConditionsVariable: conditionsVariable ?? null,
        ConditionsOperator: chainMatcher.operatorType ?? null,
        VariableValue: variableValue ?? null,
        OperatorValue: chainMatcher.value ?? null,
      });
    });

    // top level form update
    this.form.patchValue(
      {
        CounterKeyType: ruleSettings?.counterKeyType,
        Description: this.#draftRule?.ruleDescription,
        Name: this.#draftRule?.ruleName,
        RateExceedingRequests: ruleSettings?.requestCount,
        RateExceedingTimeframe: ruleSettings?.timeframe,
        ResponseAction: ruleSettings?.actionType,
        ResponseActionTimeframe: ruleSettings?.blockTime,
        Transformations: ruleSettings?.transformationTypes ?? [],
      },
      { emitEvent: false }, // no emit no form dirty!
    );

    // Set a whole form at once, so we get default state
    this.form.reset(this.form.getRawValue());
  }

  onCancelClick() {
    if (this.rateLimitId) {
      this.#router.navigate(['../../'], { relativeTo: this.#route });
    } else {
      this.#router.navigate(['../'], { relativeTo: this.#route });
    }
  }

  onRemoveMatcher(index: number) {
    this.requestMatchers.removeAt(index);
    this.requestMatchers.markAsDirty();
    this.requestMatchers.updateValueAndValidity();
    this.#previousValues = this.#requestMatchersValue();
  }

  onAddMatcher(values?: MatcherFormValue) {
    this.requestMatchers.push(this.createMatcherForm(values), { emitEvent: false });
  }

  createMatcherForm(values?: MatcherFormValue) {
    return this.#fb.group({
      ConditionsVariable: this.#fb.control<number | null>(values?.ConditionsVariable ?? null, [Validators.required]),
      ConditionsOperator: this.#fb.control<WafRuleOperatorType | null>(values?.ConditionsOperator ?? null, [Validators.required]),
      VariableValue: this.#fb.control<string | null>(values?.VariableValue ?? null),
      OperatorValue: this.#fb.control<string | null>(values?.OperatorValue ?? null, [Validators.required]),
    });
  }

  onPaste(event: ClipboardEvent, formName: string): void {
    event.preventDefault();

    const pastedData = event.clipboardData?.getData('text') || '';
    const cleanedData = cleanRuleDescription(pastedData);

    // Update the input value
    this.form.get(formName)?.patchValue(cleanedData);
  }

  onSubmitForm() {
    if (!this.form.valid) return;

    const formValues = this.form.getRawValue();
    const matchers = formValues.requestMatchers;
    const mainMatcher = matchers[0];
    const chainedRuleConditions = matchers.slice(1);
    const mainVariableType =
      this.wafRuleVariableTypes?.enumValues?.find(({ value }) => mainMatcher.ConditionsVariable !== null && value === Number(mainMatcher['ConditionsVariable']))
        ?.name ?? '';

    const rateLimitRule: Partial<CreateWafRateLimitRequest | RateLimitRule> = {
      shieldZoneId: Number(this.#shieldStore.selectedEntityId()()),
      ruleName: formValues.Name,
      ruleDescription: formValues.Description,
      ruleConfiguration: {
        variableTypes: {
          [mainVariableType]: mainMatcher['VariableValue'] ?? '',
        },
        actionType: formValues.ResponseAction ?? undefined,
        blockTime: formValues.ResponseActionTimeframe ?? undefined,
        counterKeyType: formValues.CounterKeyType ?? undefined,
        operatorType: mainMatcher['ConditionsOperator'] ?? undefined,
        requestCount: formValues.RateExceedingRequests ?? undefined,
        timeframe: formValues.RateExceedingTimeframe ?? undefined,
        transformationTypes: formValues.Transformations ?? [],
        value: mainMatcher['OperatorValue'] ?? '',
        chainedRuleConditions:
          chainedRuleConditions?.length > 0
            ? chainedRuleConditions.map((matcher) => {
                const variableType =
                  this.wafRuleVariableTypes?.enumValues?.find(
                    ({ value }) => matcher.ConditionsVariable !== null && value === Number(matcher['ConditionsVariable']),
                  )?.name ?? '';

                return {
                  variableTypes: {
                    [variableType]: matcher['VariableValue'] ?? '',
                  },
                  operatorType: matcher['ConditionsOperator'] ?? undefined,
                  value: matcher['OperatorValue'],
                };
              })
            : null,
      },
    };

    if (this.isUpdateFinished() || this.isAddFinished() || !rateLimitRule.shieldZoneId) return;

    if (this.rateLimitId) {
      this.#shieldStore.updateRateLimitRule({
        id: this.rateLimitId,
        data: rateLimitRule,
        customFields: ['rateLimits'],
      });
    } else {
      this.#shieldStore.createRateLimitRule(rateLimitRule);
    }

    // default with last form values, so it won't nag for leaving dirty site
    this.form.reset(this.form.getRawValue());
  }

  private listenToRouteParams() {
    effect(() => {
      this.rateLimitId = Number(this.#routeParams()?.rateLimitId || 0);

      if (this.rateLimitId) {
        this.#contentService.setTitle('Edit Rate Limit Rule');
        this.#draftRule = this.#shieldStore.rateLimits().find((rule) => rule.id === this.rateLimitId);

        if (this.#draftRule) {
          this.patchRateLimitForm();
        }
      }
    });
  }

  private listenToRequestMatchersChanges() {
    effect(() => {
      // Value change detection should skip unchanged forms, to speedup the checks.
      this.#requestMatchersValue()?.forEach((matcher, index) => {
        const matcherForm = this.requestMatchers.at(index);
        const isVariableValueOptional = this.wafRuleVariableTypes?.enumValues?.find((en) => en.value === matcher.ConditionsVariable)?.optionalInput;
        const isOperatorValueOptional = this.wafRuleOperatorType?.enumValues?.find((en) => en.value === matcher.ConditionsOperator)?.optionalInput;
        const variableValueControl = matcherForm.get('VariableValue');
        const operatorValueControl = matcherForm.get('OperatorValue');
        const hasConditionsVariableChanged = this.#previousValues?.[index] && this.#previousValues[index].ConditionsVariable !== matcher.ConditionsVariable;
        const hasConditionsOperatorChanged = this.#previousValues?.[index] && this.#previousValues[index].ConditionsOperator !== matcher.ConditionsOperator;

        // VariableValue hiding if optional
        if (variableValueControl && (hasConditionsVariableChanged || !this.#previousValues?.[index])) {
          // Set to empty on every change, except at init
          if (hasConditionsVariableChanged !== undefined) variableValueControl.setValue(null, { emitEvent: false });

          if (isVariableValueOptional === false) {
            variableValueControl.disable({ emitEvent: false });
          } else {
            variableValueControl.enable({ emitEvent: false });
          }
        }

        // OperatorValue hiding if optional
        if (operatorValueControl && (hasConditionsOperatorChanged || !this.#previousValues?.[index])) {
          // Set to empty on every change, except at init
          if (hasConditionsOperatorChanged !== undefined) operatorValueControl.setValue(null, { emitEvent: false });
          if (isOperatorValueOptional === false) {
            operatorValueControl.disable({ emitEvent: false });
          } else {
            operatorValueControl.enable({ emitEvent: false });
          }
        }
      });
      this.#previousValues = this.#requestMatchersValue();
    });
  }

  private listenToStoreSignals(): void {
    effect(() => {
      if (this.isAddFinished()) {
        this.#router.navigate(['../'], { relativeTo: this.#route });
      }
      if (this.isUpdateFinished()) {
        this.#router.navigate(['../../'], { relativeTo: this.#route });
      }
      if (this.shield()) {
        this.isPremium = !!this.shield()?.planType;
      }
    });
  }

  private setWafEnums() {
    toObservable(this.#shieldStore.wafEnums).subscribe((wafEnums) => {
      this.wafRuleVariableTypes = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRuleVariableType') ?? <WafMappedEnumList>[];

      this.wafRuleOperatorType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRuleOperatorType') ?? <WafMappedEnumList>[];

      this.wafRateLimitTimeframeType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRateLimitTimeframeType') ?? <WafMappedEnumList>[];

      this.wafRatelimitBlockType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRatelimitBlockType') ?? <WafMappedEnumList>[];

      this.wafRuleActionType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'RatelimitRuleActionType') ?? <WafMappedEnumList>[];

      this.wafRuleTransformationType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRuleTransformationType') ?? <WafMappedEnumList>[];

      this.wafRatelimitCounterKeyType = wafEnums?.find((apiEnum) => apiEnum.enumName === 'WafRatelimitCounterTypeEnum') ?? <WafMappedEnumList>[];
    });
  }
}

type MatcherFormValue = {
  ConditionsVariable: number | null;
  ConditionsOperator: WafRuleOperatorType | null;
  VariableValue: string | null;
  OperatorValue: string | null;
};
